import { OptionsProps } from '@avantstay/backoffice-core'
import { TaxonomyTask } from '@avantstay/case-taxonomy'

export const OLD_TAXONOMY_GROUP = 'Old Taxonomy'
export const NEW_TAXONOMY_GROUP = 'New Taxonomy'

export const oldTaxonomyGroup = (label: string) => `${OLD_TAXONOMY_GROUP} · ${label}`
export const newTaxonomyGroup = (label: string) => `${NEW_TAXONOMY_GROUP} · ${label}`

const toOption = (
  node: { taxonomyNodeId: string; label: string },
  group: string,
): OptionsProps<string> => ({
  label: node.label,
  value: node.taxonomyNodeId,
  group,
  hasFullWidth: true,
})

export interface TaxonomyCascadeSelection {
  taskNodeIds?: string[]
  serviceTypeNodeIds?: string[]
  categoryNodeIds?: string[]
}

const filterNodesBySelection = <Node extends { taxonomyNodeId: string }>(
  nodes: Node[],
  selectedNodeIds?: string[],
): Node[] => {
  if (!selectedNodeIds?.length) return nodes
  return nodes.filter(node => selectedNodeIds.includes(node.taxonomyNodeId))
}

export const buildTaxonomyTaskOptions = (tasks: TaxonomyTask[]): OptionsProps<string>[] => {
  return tasks.map(task => toOption(task, NEW_TAXONOMY_GROUP))
}

export const buildTaxonomyServiceTypeOptions = (
  tasks: TaxonomyTask[],
  { taskNodeIds }: TaxonomyCascadeSelection = {},
): OptionsProps<string>[] => {
  return filterNodesBySelection(tasks, taskNodeIds).flatMap(task =>
    task.serviceTypes.map(serviceType => toOption(serviceType, newTaxonomyGroup(task.label))),
  )
}

export const buildTaxonomyCategoryOptions = (
  tasks: TaxonomyTask[],
  { taskNodeIds, serviceTypeNodeIds }: TaxonomyCascadeSelection = {},
): OptionsProps<string>[] => {
  return filterNodesBySelection(tasks, taskNodeIds).flatMap(task =>
    filterNodesBySelection(task.serviceTypes, serviceTypeNodeIds).flatMap(serviceType =>
      serviceType.categories.map(category =>
        toOption(category, newTaxonomyGroup(`${task.label} - ${serviceType.label}`)),
      ),
    ),
  )
}

export const buildTaxonomySubcategoryOptions = (
  tasks: TaxonomyTask[],
  { taskNodeIds, serviceTypeNodeIds, categoryNodeIds }: TaxonomyCascadeSelection = {},
): OptionsProps<string>[] => {
  return filterNodesBySelection(tasks, taskNodeIds).flatMap(task =>
    filterNodesBySelection(task.serviceTypes, serviceTypeNodeIds).flatMap(serviceType =>
      filterNodesBySelection(serviceType.categories, categoryNodeIds).flatMap(category =>
        category.subcategories.map(subcategory =>
          toOption(subcategory, newTaxonomyGroup(`${serviceType.label} - ${category.label}`)),
        ),
      ),
    ),
  )
}

interface SplitTaxonomyFilterValues {
  legacyValues?: string[]
  taxonomyValues?: string[]
}

export const splitTaxonomyFilterValues = (
  values: string[] | undefined,
  taxonomyNodeIds: Set<string>,
): SplitTaxonomyFilterValues => {
  const taxonomyValues = values?.filter(value => taxonomyNodeIds.has(value)) ?? []
  const legacyValues = values?.filter(value => !taxonomyNodeIds.has(value)) ?? []

  return {
    legacyValues: legacyValues.length > 0 ? legacyValues : undefined,
    taxonomyValues: taxonomyValues.length > 0 ? taxonomyValues : undefined,
  }
}

export enum TaxonomyFilterLevel {
  Tasks = 'tasks',
  ServiceTypes = 'serviceTypes',
  Categories = 'categories',
  Subcategories = 'subcategories',
}

export const collectTaxonomyNodeIdsByLevel = (
  tasks: TaxonomyTask[],
): Record<TaxonomyFilterLevel, Set<string>> => {
  const nodeIdsByLevel: Record<TaxonomyFilterLevel, Set<string>> = {
    [TaxonomyFilterLevel.Tasks]: new Set(),
    [TaxonomyFilterLevel.ServiceTypes]: new Set(),
    [TaxonomyFilterLevel.Categories]: new Set(),
    [TaxonomyFilterLevel.Subcategories]: new Set(),
  }

  tasks.forEach(task => {
    nodeIdsByLevel[TaxonomyFilterLevel.Tasks].add(task.taxonomyNodeId)
    task.serviceTypes.forEach(serviceType => {
      nodeIdsByLevel[TaxonomyFilterLevel.ServiceTypes].add(serviceType.taxonomyNodeId)
      serviceType.categories.forEach(category => {
        nodeIdsByLevel[TaxonomyFilterLevel.Categories].add(category.taxonomyNodeId)
        category.subcategories.forEach(subcategory => {
          nodeIdsByLevel[TaxonomyFilterLevel.Subcategories].add(subcategory.taxonomyNodeId)
        })
      })
    })
  })

  return nodeIdsByLevel
}
